import * as React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './tabs';
import { cn } from '@/lib/utils';

export interface TabbedChartTab {
  id: string;
  label: string;
  badge?: string | number;
  content: React.ReactNode;
}

export interface TabbedChartProps {
  tabs: TabbedChartTab[];
  defaultTab?: string;
  title?: string;
  description?: string;
  headerActions?: React.ReactNode;
  className?: string;
  cardClassName?: string;
}

export function TabbedChart({
  tabs,
  defaultTab,
  title,
  description,
  headerActions,
  className,
  cardClassName,
}: TabbedChartProps) {
  const [activeTab, setActiveTab] = React.useState(defaultTab ?? tabs[0]?.id ?? '');

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Card className={cn('border border-slate-200', cardClassName)}>
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            {title && <CardTitle className="text-base font-semibold">{title}</CardTitle>}
            {description && (
              <CardDescription className="text-sm text-slate-500">{description}</CardDescription>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {headerActions}
          </div>
        </div>
      </CardHeader>
      <CardContent className={cn('pt-0', className)}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4 h-9 w-full justify-start gap-1 rounded-lg bg-slate-100 p-1">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className={cn(
                  'relative flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-all',
                  'data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm',
                  'data-[state=inactive]:text-slate-600 data-[state=inactive]:hover:text-slate-900'
                )}
              >
                <span className="relative flex items-center gap-1.5">
                  {activeTab === tab.id && (
                    <span className="absolute -left-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  )}
                  <span className={cn(activeTab === tab.id && 'pl-2')}>{tab.label}</span>
                </span>
                {tab.badge !== undefined && (
                  <span
                    className={cn(
                      'ml-1 inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-xs font-semibold',
                      activeTab === tab.id
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-200 text-slate-600'
                    )}
                  >
                    {tab.badge}
                  </span>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="mt-0 focus-visible:outline-none">
              {tab.content}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
